import React from 'react';
import {
  useMutation,
  useQueryClient,
} from "react-query";
import { toast } from "react-toastify";
import { format, parseJSON } from "date-fns";
import { IconDelete, IconEdit } from "./../../../assets/images/icons";
import OverviewSubheader from "../../layouts/OverviewSubheader";
import TextButton from "../../buttons/TextButton"
import ActionButton from "./../../buttons/ActionButton";
import { renderRailsErrors } from "../../../utils/rails_error_parser";
import { Form as TeamMemberForm } from './';
import titleize from "underscore.string/titleize";
import { PortalizedModalContext } from "./../../../components/PortalizedModal";
import { useSessionContext } from "./../../../contexts/SessionContext";
import { CompanyContext } from "./../../../contexts/CompanyContext";

import {
  ResponsiveTable,
  ErrorBoundary,
  Loaders,
} from "./../../../components";
import {
  useTeamMembersList,
  deleteTeamMember,
} from "./../../../queries/company";

export default function TeamMembers({companyId}) {
  const queryClient = useQueryClient();
  const {data:teamMembersList, isLoading } = useTeamMembersList({company_id: companyId})
  const teamMemberFormModal = React.useContext(PortalizedModalContext);
  const confirmationModal = React.useContext(PortalizedModalContext);
  const { selectedCompany } = React.useContext(CompanyContext);
  const { isPermitted, ACTIONS } = useSessionContext();

  const deleteTeamMemberMutation = useMutation(
    deleteTeamMember,
    {
      onSuccess: (values) => {
        queryClient.invalidateQueries(
          ["team-members-list", companyId],
          {
            exact: true,
          }
        )
        confirmationModal.closeModal();
        toast.success(`Team member deleted.`)
      },
      onError: (error) => {
        confirmationModal.closeModal();
        renderRailsErrors(error)
      },
    }
  )

  const teamMemberDeleteHandler = React.useCallback(
    (team_member_id) => {
      confirmationModal.setModalContent(
        null,
        <div>
          <div className="px-3">
            <p>Are you sure you want to delete this team member?</p>
          </div>
          <div className="p-3 text-right">
            <button
              className="btn btn-dark rounded-0 px-3"
              onClick={ () => deleteTeamMemberMutation.mutate({company_id: companyId, team_member_id: team_member_id}) }
            >
              Yes, delete team member
            </button>
          </div>
        </div>
      );
      confirmationModal.openModal()
    },
    [companyId, deleteTeamMemberMutation, confirmationModal]
  )

  const teamMemberEditHandler = React.useCallback(
    (team_member_id) => {
      const team_member  = teamMembersList.find(tm => tm.id === team_member_id)
      teamMemberFormModal.setModalContent(
        'Update Team Member',
        <TeamMemberForm teamMember={team_member} companyId={companyId} onSuccess={teamMemberFormModal.closeModal} />
      );
      teamMemberFormModal.openModal();
    },
    [teamMemberFormModal, teamMembersList, companyId]
  )

  const teamMemberCreateHandler = React.useCallback(
    () => {
      teamMemberFormModal.setModalContent(
        'Add Team Member',
        <TeamMemberForm companyId={companyId} onSuccess={teamMemberFormModal.closeModal} />
      );
      teamMemberFormModal.openModal();
    },
    [teamMemberFormModal, companyId]
  )

  const tableData = React.useMemo(
    () => {
      if (isLoading) return [];
      if(teamMembersList)
        return teamMembersList;
      else
        return []
      
    },
    [teamMembersList, isLoading]
  );

  const columns = React.useMemo(
    () => {
      if(isLoading) return [];
      if (teamMembersList){
        return [
          {
            Header: 'Member name',
            accessor: 'name',
            Cell: ({value}) => titleize(value),
          },
          {
            Header: 'Role',
            accessor: 'role',
            Cell: ({value}) => titleize(value),
          },
          {
            Header: 'Bio',
            accessor: 'bio',
            Cell: ({value}) => value.substring(0, 80),
          },
          {
            Header: 'Last updated',
            accessor: 'updated_at',
            Cell: ({value}) => format(parseJSON(value), 'MMM dd, yyyy'),
          },
          {
            Header: '',
            accessor: 'id',
            Cell: ({value:team_member_id}) => (
                isPermitted(ACTIONS.WRITE_UPDATE_TEAM_MEMBERS, selectedCompany.id) ?
                  <div className="table-action-items-container justify-content-end">
                    <ActionButton icon={IconEdit} altText="Edit team member" clickHandler={() => teamMemberEditHandler(team_member_id)} />
                    <ActionButton icon={IconDelete} altText="Delete team member" clickHandler={() => teamMemberDeleteHandler(team_member_id)} />
                  </div> :
                  null
              )
          }
        ]
      }
      else{
        return []
      }
    },
    [teamMembersList, isLoading, teamMemberDeleteHandler, teamMemberEditHandler]
  );


  if (isLoading)
    return <Loaders.ListPageLoader />

  return (
    <ErrorBoundary>
      <OverviewSubheader
        left='Team members'
        right={
          isPermitted(ACTIONS.WRITE_UPDATE_TEAM_MEMBERS, selectedCompany.id) &&
            <TextButton title='Add Team Member' onClick={teamMemberCreateHandler}/>
        }
        tooltipDesc="Team members added here are shown on your company profile but not on your pitch page during a raise. Team members are dynamic and can change over time, where the team section on pitches during a raise needs to be static and are added in the pitch section for Team. "
      />
      <ResponsiveTable title="Team Members"  columns={columns} data={tableData} />
    </ErrorBoundary>
  )
}
